import { useCallback } from 'react';
import { type UseQueryResult, useQueries } from '@tanstack/react-query';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { format } from 'date-fns';
import {
  type DeviceTrafficResponse,
  deviceTrafficQueryKey,
  fetchDeviceTrafficSeries,
} from '@/hooks/useDeviceTrafficSeries';
import { Skeleton } from '@/components/ui/skeleton';
import { CHART_MARGIN, Y_AXIS_WIDTH } from './chart';
import { formatBits } from './format';

/**
 * Seven, not eight.
 *
 * `--series-8` is red in both themes, and on a triage surface a red line must
 * never be ambiguous with the `--sev-*` chrome that means something is wrong.
 */
export const COMPARE_LIMIT = 7;

export interface ComparePort {
  interfaceId: string;
  interfaceName: string;
  /**
   * Hue slot, fixed when the port was ticked.
   *
   * Not the position in the list: unticking a port must never repaint the
   * survivors. Recolouring on filter is exactly what breaks a reader mid-way
   * through a diagnosis, when the line they had been following becomes a
   * different colour without moving.
   */
  slot: number;
}

/** One row per timestamp, a `p<slot>` column per port. */
function compareRows(
  results: UseQueryResult<DeviceTrafficResponse>[],
  ports: ComparePort[],
  direction: 'in' | 'out',
): (Record<string, number | null> & { ts: number })[] {
  const quantity = direction === 'in' ? 'bits_in' : 'bits_out';
  const byTs = new Map<number, Record<string, number | null> & { ts: number }>();
  results.forEach((result, index) => {
    const port = ports[index];
    const iface = result.data?.interfaces.find((row) => row.interfaceId === port.interfaceId);
    const points = iface?.series.find((series) => series.quantity === quantity)?.points ?? [];
    for (const [ts, value] of points) {
      let row = byTs.get(ts);
      if (!row) {
        row = { ts };
        byTs.set(ts, row);
      }
      // A null stays a null. Every block in this feature breaks its line on
      // one rather than dipping to a floor nobody observed.
      row[`p${String(port.slot)}`] = value;
    }
  });
  return [...byTs.values()].sort((a, b) => a.ts - b.ts);
}

/**
 * Is it this port, or all four uplinks?
 *
 * The escape hatch from the ranking. Lines rather than mirrored areas because
 * seven mirrored washes overlap into mud, and one direction at a time so it
 * stays one axis and one geometry.
 *
 * This **swaps** with the mirror rather than sitting beside it. That is the one
 * place in this feature where `--series-1` means an interface rather than
 * "inbound", and two meanings for one hue a few hundred pixels apart is worse
 * than either alone.
 */
export function ComparePorts({
  deviceId,
  ports,
  start,
  end,
  direction,
  syncId,
}: {
  deviceId: string;
  ports: ComparePort[];
  start: number;
  end: number;
  direction: 'in' | 'out';
  syncId: string;
}) {
  // `combine` rather than a memo over `results`: that array is new every render, and
  // React Query only re-runs a stable `combine` when a query's result actually changes.
  const combine = useCallback(
    (results: UseQueryResult<DeviceTrafficResponse>[]) => ({
      loading: results.some((result) => result.isLoading),
      rows: compareRows(results, ports, direction),
    }),
    [ports, direction],
  );
  const { loading, rows } = useQueries({
    // The hook's own key and fetch, so a port open here and in its panel is one
    // cache entry.
    queries: ports.map((port) => ({
      queryKey: deviceTrafficQueryKey(deviceId, { start, end }, port.interfaceId),
      queryFn: () => fetchDeviceTrafficSeries(deviceId, start, end, port.interfaceId),
      staleTime: 30_000,
    })),
    combine,
  });

  const spansDays = end - start > 24 * 3600 * 1000;

  if (ports.length === 0) return null;
  if (loading) return <Skeleton className="h-56 w-full" />;

  return (
    <div>
      <h3 className="text-xs font-semibold mb-1">Comparing {ports.length} ports</h3>
      <p className="text-xs text-muted-foreground mb-3">
        {direction === 'in' ? 'Into each port' : 'Out of each port'}. One direction at a time, so this stays one axis
        and one geometry.
      </p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows} syncId={syncId} syncMethod="value" margin={CHART_MARGIN}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={[start, end]}
            tickFormatter={(ts: number) => format(new Date(ts), spansDays ? 'MMM d HH:mm' : 'HH:mm')}
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            minTickGap={48}
          />
          <YAxis
            stroke="var(--fg-subtle)"
            tick={{ fontSize: 11 }}
            width={Y_AXIS_WIDTH}
            tickFormatter={(value: number) => (value === 0 ? '0' : formatBits(value))}
          />
          <Tooltip
            cursor={{ stroke: 'var(--fg-subtle)', strokeWidth: 1 }}
            content={<CompareTooltip ports={ports} spansDays={spansDays} />}
          />
          {ports.map((port) => (
            <Line
              key={port.interfaceId}
              type="monotone"
              dataKey={`p${String(port.slot)}`}
              stroke={`var(--series-${String(port.slot + 1)})`}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--card)' }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {ports.map((port) => (
          <li key={port.interfaceId} className="flex items-center gap-1.5 text-xs">
            <span
              className="inline-block w-3 h-0.5 rounded-full shrink-0"
              style={{ backgroundColor: `var(--series-${String(port.slot + 1)})` }}
              aria-hidden="true"
            />
            <span className="text-foreground">{port.interfaceName}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompareTooltip({
  active,
  payload,
  label,
  ports,
  spansDays,
}: {
  active?: boolean;
  payload?: { dataKey?: string | number; value?: number | null }[];
  label?: number;
  ports: ComparePort[];
  spansDays: boolean;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload.flatMap((entry) => {
    const slot = Number(String(entry.dataKey ?? '').replace('p', ''));
    const port = ports.find((candidate) => candidate.slot === slot);
    if (!port || entry.value === undefined || entry.value === null) return [];
    return [{ port, value: entry.value }];
  });
  if (rows.length === 0) return null;

  return (
    <div className="rounded-md border border-border bg-popover text-popover-foreground px-3 py-2 shadow-md">
      <p className="text-[11px] text-muted-foreground mb-1.5">
        {typeof label === 'number' ? format(new Date(label), spansDays ? 'MMM d, HH:mm' : 'HH:mm:ss') : ''}
      </p>
      <ul className="space-y-1">
        {rows
          .sort((a, b) => b.value - a.value)
          .map(({ port, value }) => (
            <li key={port.interfaceId} className="flex items-baseline gap-2 text-xs">
              <span
                className="inline-block w-3 h-0.5 rounded-full shrink-0"
                style={{ backgroundColor: `var(--series-${String(port.slot + 1)})` }}
                aria-hidden="true"
              />
              <span className="font-semibold tabular-nums">{formatBits(value)}</span>
              <span className="text-muted-foreground">{port.interfaceName}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}
